import {StaticPageTitle} from '@common/seo/static-page-title';
import {Trans} from '@ui/i18n/trans';
import React from 'react';
import {TextField} from '@ui/forms/input-field/text-field/text-field';
import {SearchIcon} from '@ui/icons/material/Search';
import {message} from '@ui/i18n/message';
import {useTrans} from '@ui/i18n/use-trans';
import {PageErrorMessage} from '@common/errors/page-error-message';
import {AlbumGridItem} from '@app/web-player/albums/album-grid-item';
import {ContentGrid} from '@app/web-player/playable-item/content-grid';
import {InfiniteScrollSentinel} from '@common/ui/infinite-scroll/infinite-scroll-sentinel';
import {AnimatePresence, m} from 'framer-motion';
import {opacityAnimation} from '@ui/animation/opacity-animation';
import {MediaPageNoResultsMessage} from '@app/web-player/layout/media-page-no-results-message';
import {PlayableMediaGridSkeleton} from '@app/web-player/playable-item/player-media-grid-skeleton';
import {LibraryPageSortDropdown} from '@app/web-player/library/library-page-sort-dropdown';
import {useUserLikedAlbums} from '@app/web-player/library/requests/use-user-liked-albums';
import {AdHost} from '@common/admin/ads/ad-host';
import clsx from 'clsx';
import {isMobile} from '@common/core/platform-detection';
import {NotificationDialogTrigger} from '@common/notifications/dialog/notification-dialog-trigger';
import {useParams} from 'react-router-dom';
import {
  getAlbumTypeContentFormat,
  getAlbumTypeLabel,
  normalizeAlbumTypeKey,
} from '@app/web-player/library/album-type-utils';

const sortItems = {
  'likes.created_at:desc': message('Recently added'),
  'name:asc': message('A-Z'),
  'release_date:desc': message('Release date'),
};

export function LibraryAlbumsPage() {
  const {trans} = useTrans();
  const params = useParams();
  const albumTypeParam = params.albumType;
  const albumTypeSlug = albumTypeParam ?? 'music';
  const albumTypeKey = normalizeAlbumTypeKey(albumTypeSlug);
  const label = getAlbumTypeLabel(albumTypeSlug, {musicLabel: 'Albums'});
  const contentFormat = getAlbumTypeContentFormat(albumTypeSlug);

  const query = useUserLikedAlbums('me', {
    willSortOrFilter: true,
    paginate: 'lengthAware',
    queryParams: {
      albumType: albumTypeSlug,
    },
  });
  const {
    isLoading,
    sortDescriptor,
    setSortDescriptor,
    searchQuery,
    setSearchQuery,
    items,
    isError,
    totalItems,
  } = query;

  if (isError) {
    return <PageErrorMessage />;
  }

  return (
    <div>
      <StaticPageTitle>
        {albumTypeKey === 'music' ? (
          <Trans message="Your albums" />
        ) : (
          <Trans message="Your :type" values={{type: label.toLowerCase()}} />
        )}
      </StaticPageTitle>
      <AdHost slot="general_top" className="mb-34" />
      <div className={clsx('mb-30 flex items-center justify-between gap-24')}>
        <h1 className="flex-auto overflow-hidden truncate whitespace-nowrap text-3xl font-semibold">
          {albumTypeKey === 'music' ? (
            <Trans message="My albums" />
          ) : (
            <Trans message="My :type" values={{type: label.toLowerCase()}} />
          )}
        </h1>
        {isMobile() ? <NotificationDialogTrigger /> : null}
      </div>
      <div className="flex items-center justify-between gap-24">
        <TextField
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="max-w-512 flex-auto"
          size="sm"
          startAdornment={<SearchIcon />}
          placeholder={trans(
            message('Search within :type', {
              values: {type: label.toLowerCase()},
            }),
          )}
        />
        <LibraryPageSortDropdown
          items={sortItems}
          sortDescriptor={sortDescriptor}
          setSortDescriptor={setSortDescriptor}
        />
      </div>
      <div className="mt-34">
        <AnimatePresence initial={false} mode="wait">
          {isLoading ? (
            <PlayableMediaGridSkeleton itemCount={totalItems ?? 30} />
          ) : (
            <m.div key="media-grid" {...opacityAnimation}>
              <ContentGrid
                className={clsx({
                  'content-grid-landscape': contentFormat === 'landscape',
                  'content-grid-poster': contentFormat === 'poster',
                })}
              >
                {items.map(album => (
                  <AlbumGridItem
                    key={album.id}
                    album={album}
                    contentFormat={contentFormat}
                  />
                ))}
                <InfiniteScrollSentinel query={query} />
              </ContentGrid>
            </m.div>
          )}
        </AnimatePresence>
      </div>
      {!items.length && !isLoading && (
        <MediaPageNoResultsMessage
          className="mt-34"
          searchQuery={searchQuery}
          description={
            <Trans
              message="You have not added any :type to your library yet."
              values={{type: label.toLowerCase()}}
            />
          }
        />
      )}
      <AdHost slot="general_bottom" className="mt-34" />
    </div>
  );
}
